import React, { CSSProperties } from "react";
import { useAppContext } from "../components/AppContext";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  darkModeBorder,
  darkModeWidgetBackground,
  darkModeWidgetBorderColor,
  lightModeBorder,
  lightModeWidgetBackground,
  lightModeWidgetBorderColor,
  negativeRed,
} from "../util/style";

interface TradesProps {
  handleDeleteTrade: (id: number) => void;
}

const Trades: React.FC<TradesProps> = ({ handleDeleteTrade }) => {
  const {
    allAccountString,
    getFont,
    darkMode,
    selectedAccount,
    tradesDataArray,
    adminMode,
    numFormat,
  } = useAppContext() || {};

  const tableStyles: {
    table: CSSProperties;
    cell: CSSProperties;
    rightAlignedCell: CSSProperties;
  } = {
    table: {
      borderCollapse: "collapse",
      border: `1px solid ${
        darkMode ? darkModeWidgetBorderColor : lightModeWidgetBorderColor
      }`,
      fontSize: getFont?.(10),
      width: "100%",
      background: darkMode
        ? darkModeWidgetBackground
        : lightModeWidgetBackground,
    },
    cell: {
      border: `1px solid ${darkMode ? darkModeBorder : lightModeBorder}`,
      padding: "3px 8px",
    },
    rightAlignedCell: {
      border: `1px solid ${darkMode ? darkModeBorder : lightModeBorder}`,
      padding: "3px 8px",
      textAlign: "right" as const,
    },
  };

  return (
    <div
      style={{
        height: "calc(100% - 0px)",
        padding: "10px 10px 10px 10px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "2px",
          overflow: "auto",
          background: darkMode
            ? darkModeWidgetBackground
            : lightModeWidgetBackground,
        }}
        className="hoverShadow"
      >
        {tradesDataArray?.length === 0 && <>No Trades Recorded</>}
        {tradesDataArray?.length > 0 && (
          <>
            <table
              style={{
                ...tableStyles.table,
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: darkMode
                    ? darkModeWidgetBackground
                    : lightModeWidgetBackground,
                  zIndex: 1,
                }}
              >
                <tr>
                  <th style={tableStyles.cell}>Date</th>
                  {selectedAccount === allAccountString && (
                    <th style={tableStyles.cell}>Account</th>
                  )}
                  <th style={tableStyles.rightAlignedCell}>Ticker</th>
                  <th style={tableStyles.rightAlignedCell}>Currency</th>
                  <th style={tableStyles.cell}>Buy/Sell</th>
                  <th style={tableStyles.rightAlignedCell}>Price</th>
                  <th style={tableStyles.rightAlignedCell}>Shares Traded</th>
                  <th style={tableStyles.rightAlignedCell}>Result Position</th>
                  <th style={tableStyles.rightAlignedCell}>Proceeds</th>
                  {selectedAccount !== allAccountString && (
                    <>
                      <th style={tableStyles.rightAlignedCell}>
                        Original Cost Basis
                      </th>
                      <th style={tableStyles.rightAlignedCell}>
                        Result Cost Basis
                      </th>
                    </>
                  )}
                  <th style={tableStyles.rightAlignedCell}>Trade P/L</th>
                  <th style={tableStyles.rightAlignedCell}>
                    Cumulative Ticker P/L
                  </th>
                  {adminMode && (
                    <th
                      style={{
                        ...tableStyles.rightAlignedCell,
                        width: "10px",
                      }}
                    >
                      &nbsp;
                    </th>
                  )}
                </tr>
              </thead>

              <tbody style={{}}>
                {tradesDataArray?.map((eachRow, idx) => (
                  <tr key={idx} className="hoverWatchlistBackground">
                    <td style={tableStyles.cell}>{eachRow.date}</td>
                    {selectedAccount === allAccountString && (
                      <td style={tableStyles.cell}>{eachRow.account}</td>
                    )}
                    <td style={tableStyles.cell}>{eachRow.ticker}</td>
                    <td style={tableStyles.cell}>{eachRow.currency}</td>
                    <td style={tableStyles.cell}>
                      {eachRow.adjustedShare > 0 ? "Buy" : "Sell"}
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      {numFormat?.(eachRow.adjustedPrice)}
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      {numFormat?.(eachRow.adjustedShare, true)}
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      {numFormat?.(eachRow.resultingShareCount, true)}
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      {numFormat?.(eachRow.proceeds * -1, true)}
                    </td>
                    {selectedAccount !== allAccountString && (
                      <>
                        <td style={tableStyles.rightAlignedCell}>
                          {numFormat?.(eachRow.originalCostBasis)}
                        </td>
                        <td style={tableStyles.rightAlignedCell}>
                          {numFormat?.(eachRow.resultingCostBasis)}
                        </td>
                      </>
                    )}

                    <td style={tableStyles.rightAlignedCell}>
                      {eachRow.pnl !== 0 && numFormat?.(eachRow.pnl, true)}
                    </td>

                    <td style={tableStyles.rightAlignedCell}>
                      {eachRow.pnl !== 0 &&
                        numFormat?.(eachRow?.cumulativePnL, true)}
                    </td>
                    {adminMode && (
                      <td
                        style={{
                          ...tableStyles.cell,
                          cursor: "pointer",
                          width: "10px",
                          textAlign: "center",
                        }}
                        onClick={() => {
                          if (!adminMode) return;
                          handleDeleteTrade(eachRow._id);
                        }}
                      >
                        <HighlightOffIcon
                          style={{
                            color: negativeRed,
                            fontSize: getFont?.(8),
                            transform: "translateY(1px) scale(1.75)",
                          }}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default Trades;
